/**
 * Import d'un export 1Password **`.1pif`** (« 1Password Interchange Format »,
 * 1Password 4 a 7 sur macOS et Windows : le format d'export des coffres
 * locaux, que beaucoup d'utilisateurs ont encore sur disque).
 *
 * Purement local, aucune E/S reseau. Le fichier aligne un objet JSON par
 * element, chacun suivi d'un separateur `***<uuid>***`. Chaque element est
 * RAMENE A LA FORME `.1pux` (categorie, `overview`, `details`) pour passer par
 * le meme lecteur d'element que l'export moderne (`parseOnePuxItem`) : une
 * seule correspondance categories -> types, champs types, TOTP, historique.
 *
 * Deux variantes : macOS (`typeName`, `title`, `location`, `secureContents`,
 * `openContents`) et Windows (`category`, `overview`, `details`). Les elements
 * `system.*` (dossiers, recherches, pierres tombales) et la corbeille ne sont
 * pas des entrees ; les dossiers donnent le chemin de leurs elements.
 */

import { isSecretLabel } from './csv-import.js';

type Json = null | boolean | number | string | Json[] | { [key: string]: Json };
type JsonObject = { [key: string]: Json };

/** Separateur ecrit par 1Password apres chaque element (`***5642bee8-a5ff-11dc-8314-0800200c9a66***`). */
const SEPARATOR = /^\*{3}[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\*{3}[ \t]*\r?$/imu;
const SEPARATOR_SPLIT = new RegExp(SEPARATOR.source, 'gimu');

/** `typeName` macOS -> categorie 1Password (`categoryUuid` du `.1pux`). */
const ONEPIF_CATEGORIES: Record<string, string> = {
  'webforms.WebForm': '001',
  'wallet.financial.CreditCard': '002',
  'securenotes.SecureNote': '003',
  'identities.Identity': '004',
  'passwords.Password': '005',
  'documents.Document': '006',
  'wallet.computer.License': '100',
  'wallet.financial.BankAccountUS': '101',
  'wallet.computer.Database': '102',
  'wallet.government.DriversLicense': '103',
  'wallet.government.HuntingLicense': '104',
  'wallet.membership.Membership': '105',
  'wallet.government.Passport': '106',
  'wallet.membership.RewardProgram': '107',
  'wallet.government.SsnUS': '108',
  'wallet.computer.Router': '109',
  'wallet.computer.UnixServer': '110',
  'wallet.onlineservices.Email.v2': '111',
};

/** Cles du contenu deja lues ; toute autre valeur texte des anciens exports devient un champ, jamais perdue. */
const KNOWN_CONTENT_KEYS = new Set(['fields', 'sections', 'notesPlain', 'password', 'passwordHistory', 'URLs', 'htmlForm', 'documentAttributes', 'expiry_mm', 'expiry_yy']);

/**
 * Cle plate d'un ancien export dont la valeur est un secret : masquee. Le
 * libelle est lu comme une colonne CSV (`isSecretLabel`) : `pinCode`,
 * `private_key`, `recovery_codes` ou `accountNo` disent le meme secret ;
 * `pin$` garde les cles collees (`bankpin`) que la lecture en mots ne coupe pas.
 */
function isSecretKey(key: string): boolean {
  return /pin$/iu.test(key) || isSecretLabel(key);
}

function asObject(value: Json | undefined): JsonObject | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? value : null;
}

function asArray(value: Json | undefined): Json[] {
  return Array.isArray(value) ? value : [];
}

function asString(value: Json | undefined): string {
  return typeof value === 'string' ? value : typeof value === 'number' ? String(value) : '';
}

/** Element `.1pif` ramene a la forme `.1pux`, avec le chemin de son dossier. */
export interface OnePifItem {
  readonly item: JsonObject;
  readonly folderPath: string[];
}

/** Un export `.1pif` se reconnait a un objet JSON en tete et a son separateur `***<uuid>***`. */
export function isOnePifText(text: string): boolean {
  return /^[\s\uFEFF]*\{/u.test(text) && SEPARATOR.test(text);
}

/** Champ de section `.1pif` (`k` genre, `n` identifiant, `t` libelle, `v` valeur) -> champ de section `.1pux`. */
function sectionField(field: JsonObject): JsonObject | null {
  const raw = field['v'];
  if (raw === undefined || raw === null || (typeof raw === 'string' && raw.trim().length === 0)) return null;
  const id = asString(field['n']);
  const kind = asString(field['k']) || 'string';
  // Graine TOTP : 1Password la range dans un champ `concealed` dont l'identifiant commence par `TOTP_`.
  const valueKind = id.startsWith('TOTP_') ? 'totp' : kind === 'URL' ? 'url' : kind;
  return { id, title: asString(field['t']), value: { [valueKind]: raw } };
}

/** Champs de formulaire (`designation` username / password) -> `loginFields` du `.1pux`. */
function loginField(field: JsonObject): JsonObject {
  return {
    value: asString(field['value']),
    designation: asString(field['designation']),
    name: asString(field['name']),
    // `type` 'P' = champ mot de passe du formulaire, `fieldType` dans le `.1pux`.
    fieldType: asString(field['type']),
  };
}

/** Un element `.1pif` (macOS ou Windows) -> element `.1pux`. */
function toOnePuxItem(raw: JsonObject): JsonObject {
  const overview = asObject(raw['overview']) ?? {};
  const open = asObject(raw['openContents']) ?? {};
  const contents = asObject(raw['secureContents']) ?? asObject(raw['details']) ?? {};
  const typeName = asString(raw['typeName']);
  const windowsCategory = asString(raw['category']);
  const category = /^\d{3}$/u.test(windowsCategory)
    ? windowsCategory
    : ONEPIF_CATEGORIES[typeName] ?? (typeName.startsWith('wallet.onlineservices.') ? '001' : '');

  const loginFields = asArray(contents['fields']).map(asObject).filter((field): field is JsonObject => field !== null).map(loginField);
  const sections: JsonObject[] = asArray(contents['sections'])
    .map(asObject)
    .filter((section): section is JsonObject => section !== null)
    .map((section) => ({
      fields: asArray(section['fields'])
        .map(asObject)
        .filter((field): field is JsonObject => field !== null)
        .map(sectionField)
        .filter((field): field is JsonObject => field !== null),
    }));

  // Identifiant (compte en ligne) : les anciennes fiches portent `username` / `password` dans une section, pas dans
  // le formulaire. Ils vont a l'entree comme le ferait le formulaire.
  if (category === '001') {
    for (const designation of ['username', 'password']) {
      if (loginFields.some((field) => field['designation'] === designation && asString(field['value']).length > 0)) continue;
      for (const section of sections) {
        const fields = section['fields'] as JsonObject[];
        const index = fields.findIndex((field) => field['id'] === designation);
        if (index < 0) continue;
        const [field] = fields.splice(index, 1);
        const value = asObject(field!['value']) ?? {};
        loginFields.push({ value: asString(Object.values(value)[0]), designation, name: designation, fieldType: designation === 'password' ? 'P' : 'T' });
        break;
      }
    }
  }

  // Anciens exports : valeurs a plat dans le contenu (`ccnum`, `cvv`, `expiry_mm`…). Elles rejoignent une section
  // sous leur cle, qui sert d'identifiant : les champs types de la categorie les reconnaissent.
  const knownIds = new Set(sections.flatMap((section) => (section['fields'] as JsonObject[]).map((field) => field['id'])));
  const flat: JsonObject[] = [];
  const month = asString(contents['expiry_mm']).trim();
  const year = asString(contents['expiry_yy']).trim();
  if (!knownIds.has('expiry') && /^\d{1,2}$/u.test(month) && /^\d{2}(?:\d{2})?$/u.test(year)) {
    const fullYear = year.length === 2 ? `20${year}` : year;
    flat.push({ id: 'expiry', title: 'expiry', value: { monthYear: Number(`${fullYear}${month.padStart(2, '0')}`) } });
  }
  for (const [key, value] of Object.entries(contents)) {
    if (KNOWN_CONTENT_KEYS.has(key) || knownIds.has(key)) continue;
    if (typeof value !== 'string' && typeof value !== 'number') continue;
    if (asString(value).trim().length === 0) continue;
    flat.push({ id: key, title: key, value: { [isSecretKey(key) ? 'concealed' : 'string']: value } });
  }
  if (flat.length > 0) sections.push({ fields: flat });

  const urls: JsonObject[] = [...asArray(contents['URLs']), ...asArray(overview['URLs'])]
    .map(asObject)
    .map((url) => asString(url?.['url']) || asString(url?.['u']))
    .filter((url) => url.length > 0)
    .map((url) => ({ url }));
  const location = asString(raw['location']) || asString(overview['url']);

  // `time` en secondes ; certains exports Windows l'ecrivent en millisecondes.
  const passwordHistory: JsonObject[] = asArray(contents['passwordHistory']).map((entry) => {
    const object = asObject(entry) ?? {};
    const time = object['time'];
    return { value: asString(object['value']), ...(typeof time === 'number' ? { time: time >= 1e12 ? time / 1000 : time } : {}) };
  });

  const favorite = [open['faveIndex'], raw['faveIndex'], overview['faveIndex']].find((value) => typeof value === 'number');
  // Creation de l'element (secondes, parfois millisecondes sous Windows) : avec l'historique, elle date le mot de passe
  // comme pour le `.1pux` — 1Password garde chaque ancien mot de passe. Jamais `updatedAt` : un titre retouche ne
  // rajeunit pas le mot de passe.
  const createdAt = raw['createdAt'];
  return {
    categoryUuid: category,
    ...(typeof createdAt === 'number' ? { createdAt: createdAt >= 1e12 ? createdAt / 1000 : createdAt } : {}),
    ...(typeof favorite === 'number' ? { favIndex: favorite } : {}),
    overview: {
      title: asString(raw['title']) || asString(overview['title']),
      url: location,
      urls,
      tags: [...asArray(open['tags']), ...asArray(overview['tags'])],
    },
    details: {
      loginFields,
      notesPlain: asString(contents['notesPlain']),
      sections,
      password: asString(contents['password']),
      passwordHistory,
      ...(contents['documentAttributes'] !== undefined ? { documentAttributes: contents['documentAttributes'] } : {}),
    },
  };
}

/** Chemin d'un dossier `.1pif`, de la racine vers la feuille ; une boucle de dossiers s'arrete, jamais infinie. */
function folderPathOf(uuid: string, folders: ReadonlyMap<string, { title: string; parent: string }>): string[] {
  const path: string[] = [];
  const seen = new Set<string>();
  for (let current = uuid; current.length > 0 && !seen.has(current); ) {
    seen.add(current);
    const folder = folders.get(current);
    if (folder === undefined) break;
    if (folder.title.length > 0) path.unshift(folder.title);
    current = folder.parent;
  }
  return path;
}

/**
 * Elements d'un export `.1pif`, ramenes a la forme `.1pux` : corbeille et elements `system.*` ecartes.
 *
 * @throws {Error} element JSON illisible : l'import entier est refuse plutot qu'un element perdu en silence
 */
export function onePifItems(text: string): OnePifItem[] {
  const raws: JsonObject[] = [];
  for (const [index, chunk] of text.replace(/^\uFEFF/u, '').split(SEPARATOR_SPLIT).entries()) {
    const trimmed = chunk.trim();
    if (trimmed.length === 0) continue;
    let parsed: Json;
    try {
      parsed = JSON.parse(trimmed) as Json;
    } catch {
      throw new Error(`export 1Password (.1pif) : element n°${index + 1} illisible`);
    }
    const object = asObject(parsed);
    if (object === null) throw new Error(`export 1Password (.1pif) : element n°${index + 1} illisible`);
    raws.push(object);
  }
  const folders = new Map<string, { title: string; parent: string }>();
  for (const raw of raws) {
    if (asString(raw['typeName']) !== 'system.folder.Regular') continue;
    folders.set(asString(raw['uuid']), { title: asString(raw['title']).trim(), parent: asString(raw['folderUuid']) });
  }
  const items: OnePifItem[] = [];
  for (const raw of raws) {
    if (raw['trashed'] === true || asString(raw['typeName']).startsWith('system.')) continue;
    items.push({ item: toOnePuxItem(raw), folderPath: folderPathOf(asString(raw['folderUuid']), folders) });
  }
  return items;
}
